import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AuthLayout, Spinner } from '@lp/portal-ui';
import { type StaffSelf, portalHandoffSchema } from '@lp/shared';
import { api } from '../api.ts';
import { errorMessage } from '../lib/errors.ts';
import { Notice } from '../components/ui.tsx';

/**
 * Landing page of the platform admin portal's 「登入品牌後台」 link (`/handoff?token=…`): trades the
 * one-time token for a brand-portal session, then opens the dashboard.
 */
export default function HandoffPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const valid = portalHandoffSchema.safeParse({ token }).success;
  const [error, setError] = useState<string | null>(valid ? null : '登入連結不完整，請回到平台管理後台再按一次「登入品牌後台」。');
  // The token works once; React's dev double-run of effects must not send it twice.
  const started = useRef(false);

  useEffect(() => {
    if (!valid || started.current) return;
    started.current = true;
    api
      .post<{ user: StaffSelf }>('/auth/handoff', { token })
      .then(() => navigate('/', { replace: true }))
      .catch((e: unknown) =>
        setError(
          errorMessage(e, {
            UNAUTHENTICATED: '登入連結無效、已使用或已過期（連結只可使用一次，5 分鐘內有效）。請回到平台管理後台重新產生。',
            CODE_INVALID: '登入連結無效或已使用，請回到平台管理後台重新產生。',
            CODE_EXPIRED: '登入連結已過期，請回到平台管理後台重新產生。',
            NOT_FOUND: '登入連結無效或已使用，請回到平台管理後台重新產生。',
            FORBIDDEN: '此帳戶不可登入品牌後台。',
          }),
        ),
      );
  }, [valid, token, navigate]);

  return (
    <AuthLayout subtitle="品牌後台" title="登入品牌後台">
      {error ? (
        <>
          <Notice tone="danger" title="未能登入品牌後台">
            {error}
          </Notice>
          <Link to="/login" replace className="mt-6 block text-center text-sm font-medium text-brand-700 hover:underline">
            改用電郵及密碼登入
          </Link>
        </>
      ) : (
        <Spinner label="正在登入…" />
      )}
    </AuthLayout>
  );
}
